import { MoreVertical, Pencil, PenLine, Copy, Trash2, FileText, FlaskConical } from 'lucide-react'
import { useTranslation } from '@/i18n'
import {
  IntuiDropdownMenu, IntuiDropdownMenuTrigger, IntuiDropdownMenuContent,
  IntuiDropdownMenuItem, IntuiDropdownMenuSeparator,
} from '@/components/intueri/IntuiDropdownMenu'
import { cn } from '@/lib/utils'
import type { Workflow } from './types'

export interface WorkflowRowActions {
  /** "Edit" — opens the workflow in the builder. */
  onOpen?: (workflow: Workflow) => void
  /** "View Report" — opens the workflow's report tab (run history). */
  onViewReport?: (workflow: Workflow) => void
  /** "Test Workflow" — opens the builder and starts a manual test run. */
  onTest?: (workflow: Workflow) => void
  onDuplicate?: (workflow: Workflow) => void
  onRename?: (workflow: Workflow) => void
  onDelete?: (workflow: Workflow) => void
}

/**
 * Row/card kebab menu (Figma §1 "Roles"). Everyone gets View Report + Test
 * Workflow; managers (Admin/Owner — `canManage`) additionally get Edit ·
 * Duplicate · Delete (rename/delete only for non-built-ins).
 */
export default function WorkflowRowMenu({
  workflow,
  canManage = true,
  hidden = false,
  triggerClassName,
  defaultOpen,
  ...actions
}: {
  workflow: Workflow
  canManage?: boolean
  /** Suppress the whole menu — annotators are read-only everywhere, so even
   *  View Report / Test must not be offered (main's annotator-gating rule). */
  hidden?: boolean
  triggerClassName?: string
  /** Render opened — for tests (Radix triggers don't open via synthetic clicks in jsdom). */
  defaultOpen?: boolean
} & WorkflowRowActions) {
  const { t } = useTranslation()
  if (hidden) return null
  return (
    <IntuiDropdownMenu defaultOpen={defaultOpen}>
      <IntuiDropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('workflow:row.more', { defaultValue: 'More actions' })}
          className={cn(
            'grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            triggerClassName,
          )}
        >
          <MoreVertical className="size-4" />
        </button>
      </IntuiDropdownMenuTrigger>
      <IntuiDropdownMenuContent align="end">
        <IntuiDropdownMenuItem onSelect={() => actions.onViewReport?.(workflow)}>
          <FileText className="size-4" />
          {t('workflow:row.view_report', { defaultValue: 'View Report' })}
        </IntuiDropdownMenuItem>
        <IntuiDropdownMenuItem onSelect={() => actions.onTest?.(workflow)}>
          <FlaskConical className="size-4" />
          {t('workflow:row.test', { defaultValue: 'Test Workflow' })}
        </IntuiDropdownMenuItem>
        {canManage && (
          <>
            <IntuiDropdownMenuSeparator />
            <IntuiDropdownMenuItem onSelect={() => actions.onOpen?.(workflow)}>
              <Pencil className="size-4" />
              {t('workflow:row.edit', { defaultValue: 'Edit' })}
            </IntuiDropdownMenuItem>
            <IntuiDropdownMenuItem onSelect={() => actions.onDuplicate?.(workflow)}>
              <Copy className="size-4" />
              {t('workflow:row.duplicate', { defaultValue: 'Duplicate' })}
            </IntuiDropdownMenuItem>
            {/* Built-in templates are read-only: rename + delete only for user workflows. */}
            {!workflow.builtin && (
              <>
                <IntuiDropdownMenuItem onSelect={() => actions.onRename?.(workflow)}>
                  <PenLine className="size-4" />
                  {t('workflow:row.rename', { defaultValue: 'Rename' })}
                </IntuiDropdownMenuItem>
                <IntuiDropdownMenuSeparator />
                <IntuiDropdownMenuItem
                  variant="destructive"
                  onSelect={() => actions.onDelete?.(workflow)}
                >
                  <Trash2 className="size-4" />
                  {t('workflow:row.delete', { defaultValue: 'Delete' })}
                </IntuiDropdownMenuItem>
              </>
            )}
          </>
        )}
      </IntuiDropdownMenuContent>
    </IntuiDropdownMenu>
  )
}
